// 큰 간헐천: 18~70초마다 작은·중간·큰 분출 중 하나. 4초 전부터 부글부글, 돔이 부풀고, 물기둥·물보라. 프로토타입 2664–2698.
// 미니 간헐천·퍼핀 소품은 items/fx.ts의 itemFxState로 이 분출에 맞춰 움직인다.
import type * as THREE from 'three';
import { itemFxState } from '../../items/fx';
import type { Geyser } from './scene';

export type GeyserKind = 'small' | 'medium' | 'big';
const KINDS: { name: GeyserKind; H: number; dur: number; w: number }[] = [
  { name: 'small', H: 6, dur: 3, w: 0.3 }, { name: 'medium', H: 14, dur: 4.5, w: 0.45 }, { name: 'big', H: 26, dur: 6.5, w: 0.25 },
];

export interface GeyserHooks {
  /** 분출 4초 전 (중간·큰 분출이면 안내 문구). */
  onWarn?(kind: GeyserKind): void;
  /** 물기둥이 솟는 순간 (주변 카피바라 반응, 안내 문구). */
  onFire?(kind: GeyserKind, t: number): void;
}

export interface GeyserState {
  next: number;
  ev: { k: (typeof KINDS)[number]; start: number; fired: boolean; warned: boolean } | null;
  /** 다음 분출 종류를 정해 둔다 (lab 확인용). */
  forceKind: GeyserKind | null;
  tick(t: number): void;
}

function pick() {
  let r = Math.random(), k = 0;
  for (; k < KINDS.length - 1; k++) { r -= KINDS[k]!.w; if (r < 0) break; }
  return KINDS[k]!;
}

export function createGeyser(g: Geyser, hooks: GeyserHooks = {}): GeyserState {
  const S: GeyserState = {
    next: 12, ev: null, forceKind: null,
    tick(t) {
      if (!S.ev && t >= S.next - 4) { const k = (S.forceKind && KINDS.find((x) => x.name === S.forceKind)) || pick(); S.forceKind = null; S.ev = { k, start: S.next, fired: false, warned: false }; }
      const ev = S.ev;
      if (!ev) {
        itemFxState.gh = 0; itemFxState.pre = 0; g.jet.visible = g.core.visible = false; g.dome.scale.setScalar(0.001);
        g.spray.forEach((s) => { s.material.opacity = 0; }); g.column.forEach((s) => { s.material.opacity = 0; });
        (g.pool.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.15;
        return;
      }
      const K = ev.k, sc = K.H / 26, e = t - ev.start, rise = 1.0, fall = 2.5;
      if (e > K.dur + fall + 6) { S.ev = null; S.next = t + 18 + Math.random() * 52; return; }
      if (!ev.warned) { ev.warned = true; hooks.onWarn?.(K.name); }
      const pre = Math.max(0, Math.min(1, (e + 4) / 4));
      (g.pool.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.15 + (0.25 + 0.4 * sc) * pre * (e < K.dur ? 1 : 0) * (0.5 + 0.5 * Math.sin(t * 18));
      itemFxState.pre = e < 1.8 ? Math.max(0, Math.min(1, (e + 4) / 5.8)) : 0;
      const dmax = 0.35 + 0.65 * sc, dm = e < 0 ? pre * dmax * 0.35 : e < 1.8 ? dmax * (0.35 + 0.65 * (e / 1.8)) : 0.001;
      g.dome.scale.set(dm, dm * (0.7 + 0.3 * Math.sin(t * 14)), dm);
      const e2 = e - 1.8;
      let h = 0;
      if (e2 >= 0 && e2 < K.dur) h = K.H * Math.min(1, 1 - Math.pow(1 - Math.min(1, e2 / rise), 3)) * (1 + 0.08 * Math.sin(e2 * 5.3) * (e2 > rise ? 1 : 0));
      else if (e2 >= K.dur && e2 < K.dur + fall) h = K.H * (1 - (e2 - K.dur) / fall);
      const flick = 1 + 0.06 * Math.sin(t * 23) + 0.04 * Math.sin(t * 37), wd = 0.4 + 0.6 * sc;
      g.jet.visible = g.core.visible = h > 0.05;
      g.jet.scale.set((1 + h * 0.03) * wd, Math.max(0.01, h * flick), (1 + h * 0.03) * wd); g.core.scale.set(0.45 * wd, Math.max(0.01, h * 1.08 * flick), 0.45 * wd);
      // 물기둥 셰이더와 기둥을 감싼 김
      g.jetMat.uniforms.uT!.value = t; g.jetMat.uniforms.opacity!.value = 0.9 * Math.min(1, h / 4);
      g.column.forEach((s, i) => {
        const f = (i + 0.5) / g.column.length, y = h * f;
        s.position.set(Math.sin(t * 0.7 + i) * 0.4, y, Math.cos(t * 0.6 + i) * 0.4);
        const scl = 2.2 + f * 7 + Math.sin(t * 3 + i) * 0.4; s.scale.set(scl, scl * 1.3, 1); s.material.opacity = h > 0.5 ? 0.55 * (1 - f * 0.4) : 0;
      });
      itemFxState.gh = h;
      const cloud = 0.35 + 0.65 * sc, life0 = K.dur * 0.9 + 1.5;
      g.spray.forEach((sp, i) => {
        const life = Math.max(0, Math.min(1, (e2 - i * 0.18 * sc) / life0)), a = i * 2.39;
        sp.position.set(Math.sin(a) * (0.8 + life * 7 * cloud), Math.max(1, h * 0.9) + life * 6 * cloud - life * life * 10 * cloud, Math.cos(a) * (0.8 + life * 7 * cloud));
        const s2 = (5 + life * 13) * cloud; sp.scale.set(s2, s2, 1); sp.material.opacity = (i < 8 + 18 * sc) && life > 0 && life < 1 ? Math.sin(life * Math.PI) * 0.8 : 0;
      });
      if (e2 >= 0 && !ev.fired) { ev.fired = true; if (K.name !== 'small') itemFxState.flapUntil = t + K.dur + 2; hooks.onFire?.(K.name, t); }
    },
  };
  return S;
}
